import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useImplementationsQuery } from '~/core/routes/settings/implementations/implementations.gql.generated';

const BASE_URL = '/settings/implementations';

type CrumbProps = {
  label?: React.ReactNode;
  to?: string;
  leftAddon?: React.ReactNode;
  separator?: string;
};
const Crumb = ({ label, to, leftAddon, separator = '>' }: CrumbProps) => {
  const linkProps = to ? { as: Link, to } : {};

  return (
    <BreadcrumbItem separator={separator} spacing="0.75rem">
      <BreadcrumbLink {...linkProps}>
        <Flex gap="0.4rem" alignItems="center">
          {leftAddon}
          <Typography
            variant="detail-muted"
            TextProps={{
              maxW: '200px',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
        </Flex>
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
};

const FacilityCrumb = () => {
  const { implementationId, facilityId } = useParams();
  const { facilities } = useCreditFacilitiesOrThrow();
  const facility = facilities.find((f) => f.id === facilityId);

  if (!facility) return null;

  return (
    <Crumb
      label={facility.name}
      to={`${BASE_URL}/${implementationId}/facilities/${facilityId}`}
      leftAddon={<Icon icon="building-columns" size="md" />}
      separator=""
    />
  );
};

const ImplementationCrumb = () => {
  const { implementationId } = useParams();
  const { user } = useUserAuthOrThrow();

  const { error, data } = useImplementationsQuery({
    variables: { organizationId: user.organizationId },
  });
  if (error) throw error;

  const implementation = data?.organization.implementations.find(
    (i) => i.id === implementationId,
  );

  if (!implementation) return null;

  return (
    <Crumb
      label={implementation.name}
      to={`${BASE_URL}/${implementationId}`}
      leftAddon={<Icon icon="keyboard" size="md" />}
    />
  );
};

export const BreadCrumbs = () => (
  <Breadcrumb fontSize="small" separator=">">
    <Crumb to="/settings" label="Settings" />
    <ImplementationCrumb />
    <FacilityCrumb />
  </Breadcrumb>
);
